import * as Types from '@aeppic/types'
import { DateTime, Duration as DateTimeDuration } from 'luxon'

// import Document from './document'
import version from '@aeppic/shared/version'
import { normalizeCurrencyBaseUnitAmount } from '@aeppic/shared/currencies'

import { Form, FileField, TagMatch, TagMatchOptions, FieldValidationErrors, DataValidationErrors, extractDatePartFromDateTimeString } from './form.js'
import type { FieldInfo, TypeValidationOptions } from './form.js'
import { default as Revision, RevisionType } from './revision.js'

import { LockChanges, asReference } from './model.js'

const DEFAULT_MIME_TYPE = 'text/plain'
const MAX_ERROR_VALUE_LENGTH = 1000
const FILE_INFO_TIMESTAMP_FIELDS = ['created', 'read', 'modified', 'imported'] as const

type DeferredRecalculationHandle = {
  type: 'idle' | 'animationFrame' | 'timeout'
  id: any
}

export interface AddReferenceOptions {
  updateVersionIfAlreadyKnown?: boolean
  allowMultiple?: boolean,
  addToBeginning?: boolean
}

export interface AddLockOptions {
  updateVersionIfAlreadyKnown?: boolean
}

export interface FileSetOptions {
  name?: string
  type?: string
}

export interface RecoverFormUpgradeResult {
  previousDocument: Types.Document
  copiedFields: string[]
  skippedFields: string[]
  failedFields: { name: string, error: string }[]
}

const DEFAULT_ADDREFERENCE_OPTIONS: AddReferenceOptions = {
  updateVersionIfAlreadyKnown: false,
  allowMultiple: false,
  addToBeginning: false
}

function resolveDirtyFieldLocale(): string {
  const globalObject = typeof globalThis !== 'undefined' ? globalThis as any : null
  const documentLanguage = globalObject?.document?.documentElement?.lang
  const navigatorLanguage = globalObject?.navigator?.language

  return documentLanguage || navigatorLanguage || 'en-US'
}

/**
 * @internal
 */
export interface Options {
  createFileURL: typeof URL.createObjectURL
  insertOrUpdate?: boolean
}

export interface IChangeOperation {
  type: string
}

export interface FieldChangeOperation extends IChangeOperation {
  field: string
}

export interface AtomicIncrement extends FieldChangeOperation {
  type: 'increment'
  delta: number
}

export interface AtomicDecrement extends FieldChangeOperation {
  type: 'decrement'
  delta: number
}

export type ChangeOperation = AtomicIncrement | AtomicDecrement

/** @deprecated Proxy mode is now always enabled */
export function enableProxyMode() {}

/** @deprecated Proxy mode is now always enabled */
export function disableProxyMode() {}

export type RequiredField = {
  text?: string, html?: string
}

export type RequiredFields = {
  [fieldName: string]: RequiredField
}

export class EditableDocument implements Types.Document {
  private __editVersion = version()
  private __insertOrUpdateMode = false

  private __data: Types.DocumentData
  private __dataProxy: Types.DocumentData
  private __revisions: Revision[]  = []
  private __form: Form
  private __pendingFileUploadFields: Set<string> = null
  private __locks: Types.Reference[] = null
  private __cloneOf: Types.Reference = null
  private __originalCloneOf: Types.Reference = null
  private __hidden: boolean
  private __readonly: boolean
  private __frozen: Types.ChangeSourceInfo
  private __lockChanges: LockChanges[] = []
  private __changeOperations: ChangeOperation[] = []
  private __writtenSinceRevision = new Set()
  private __validationErrors: DataValidationErrors = null
  private __onMutation: (() => void) | null = null
  private __dirtyFields: Set<string> = new Set()
  private __dirtyMeta = { locks: false, hidden: false, readonly: false, frozen: false }
  private __previousForm: Form | null = null
  private __formRefOverride: Types.Reference | null = null
  private __previousRef: any | null = null
  private __recalculationPending = false
  private __deferredRecalculationHandle: DeferredRecalculationHandle | null = null

  /** @internal */
  public __base: Types.Document

  /** @internal */
  private __createFileURL: typeof URL.createObjectURL
  
  /** @internal */
  constructor(document: Types.Document, form: Form, options?: Options) {
    if (options) {
      this.__createFileURL = options.createFileURL
      this.__insertOrUpdateMode = options.insertOrUpdate === true
    }

    this.updateBase(document, form)
    // Object.preventExtensions(this) // Cannot do this. Vue reactivity would be impacted
  }

  public cloneAsDocument(): Types.Document {
    return JSON.parse(this.toJSONString())
  }

  public toString() {
    return this.toJSONString()
  }

  public toJSONString() {
    return JSON.stringify(this._toDocument())
  }

  private _toDocument() {
    const doc: Types.Document = {
      id: this.id,
      p: this.p,
      f: this.f,
      previous: this.__base.previous,
      v: this.__editVersion,
      data: this.__data,
      locks: this.__locks,
      modified: this.__base.modified,
      created: this.__base.created,
      t: this.__base.t,
      ct: this.__base.ct,
      a: this.a,
      a_depth: this.a_depth,
      a_forms: this.a_forms,
      inheritedLocks: this.inheritedLocks,
      valid: this.__validationErrors == null,
    }

    if (this.__frozen) {
      doc.frozen = this.__frozen
      doc.readonly = true
    }

    if (this.__readonly) {
      doc.readonly = true
    }

    if (this.__hidden) {
      doc.hidden = true
    }

    return doc
  }

  public static isEditableDocument(document: any): document is EditableDocument {
    return (document && document.constructor === EditableDocument)
  }

  /**
   * Whenever the user commits an editable document a revision is
   * created. 
   */
  get editVersion() {return this.__editVersion}

  get revisions() {return this.__revisions.slice(0)}
  get hasRevisions() {return this.__revisions.length > 0}
  get revisionCount() {return this.__revisions.length}

  get hidden() {return this.__hidden}
  set hidden(value: boolean) {
    if (this.isFrozen) {
      throw new Error('Cannot change hidden state of a frozen document')
    }
    this.__hidden = value
    this.__dirtyMeta.hidden = (this.__hidden !== (this.__base.hidden === true))
    this._notifyMutation()
  }

  // Readonly is a soft readonly. It protects from accidental changes
  // inside controllers etc, but it is not a lock. When document changes
  // are sent to the server the server will accept the write even if the
  // document is readonly. Readonly is a client side protection only.
  //
  // To actually prevent unauthorized changes use locks.
  get readonly() {return this.__readonly || this.isFrozen}
  set readonly(value: boolean) {
    if (this.isFrozen) {
      throw new Error('Cannot change readonly state of a frozen document')
    }
    this.__readonly = value
    this.__dirtyMeta.readonly = (this.__readonly !== (this.__base.readonly === true))
    this._notifyMutation()
  }

  // Frozen is an irreversible data immutability marker. Once a document is
  // frozen it permanently becomes readonly and no further data/form changes
  // are allowed. The frozen field records who froze it and when.
  get isFrozen() { return !!this.__frozen }
  get frozen(): Types.ChangeSourceInfo { return this.__frozen }
  set frozen(value: Types.ChangeSourceInfo) {
    this.freeze(value)
  }

  freeze(source?: Types.ChangeSourceInfo) {
    if (this.isFrozen) {
      return
    }

    this.__frozen = source ?? {
      at: new Date().toISOString(),
      by: '',
      backedBy: '',
      impersonatedBy: '',
      from: '',
      client: ''
    }

    this.__readonly = true
    this.__dirtyMeta.frozen = !this.__base.frozen
    this.__dirtyMeta.readonly = (this.__readonly !== (this.__base.readonly === true))
    this._notifyMutation()
  }

  get hasFormChange(): boolean { return !!this.__previousForm }

  changeForm(targetForm: Form): Types.Document {
    if (this.isFrozen) {
      throw new Error('Cannot change form of a frozen document')
    }

    if (this.isDirty) {
      throw new Error('Cannot change form on a document with pending mutations. Save or revert first.')
    }

    if (this.__previousForm) {
      throw new Error('Form has already been changed. Revert before changing again.')
    }

    const previousDocument = this.__base

    this.__previousForm = this.__form
    this.__form = targetForm
    this.__formRefOverride = { id: targetForm.id, v: targetForm.v, text: targetForm.name }
    this.__previousRef = { v: this.__base.v, f: { id: this.__base.f.id, v: this.__base.f.v, text: this.__base.f.text } }

    const newData = targetForm.cloneDocumentData(targetForm.info.default)
    targetForm.copyFieldsFromDifferentForm(newData, this.__base.data, this.__previousForm)

    this.__data = newData
    this.__dataProxy = this._buildProxy(this.__data)

    this.recalculateAndValidate()
    this._trackAllFieldsDirty()
    this._notifyMutation()

    return previousDocument
  }

  upgradeForm(targetForm: Form): Types.Document {
    if (targetForm.id !== this.__form.id) {
      throw new Error(`upgradeForm requires the same form id. Current: ${this.__form.id}, target: ${targetForm.id}`)
    }

    return this.changeForm(targetForm)
  }

  recoverUpgradeForm(targetForm: Form, sourceData: any = this.__base.data): RecoverFormUpgradeResult {
    if (this.isFrozen) {
      throw new Error('Cannot change form of a frozen document')
    }

    if (this.isDirty) {
      throw new Error('Cannot recover form upgrade on a document with pending mutations. Save or revert first.')
    }

    if (targetForm.id !== this.__base.f.id) {
      throw new Error(`recoverUpgradeForm requires the same form id. Current: ${this.__base.f.id}, target: ${targetForm.id}`)
    }

    const previousDocument = this.__base

    this.__previousForm = targetForm
    this.__form = targetForm
    this.__formRefOverride = { id: targetForm.id, v: targetForm.v, text: targetForm.name }
    this.__previousRef = { v: this.__base.v, f: { id: this.__base.f.id, v: this.__base.f.v, text: this.__base.f.text } }

    this.__data = targetForm.cloneDocumentData(targetForm.info.default)
    this.__dataProxy = this._buildProxy(this.__data)

    const copiedFields: string[] = []
    const skippedFields: string[] = []
    const failedFields: { name: string, error: string }[] = []

    for (const field of targetForm.fields) {
      if (!sourceData || !(field.name in sourceData)) {
        skippedFields.push(field.name)
        continue
      }

      try {
        this.setField(field.name, coerceFieldCardinality(field, sourceData[field.name]), { ignoreReadonly: true })
        copiedFields.push(field.name)
      } catch (error) {
        failedFields.push({ name: field.name, error: error instanceof Error ? error.message : String(error) })
      }
    }

    this.recalculateAndValidate()
    this._trackAllFieldsDirty()
    this._notifyMutation()

    return { previousDocument, copiedFields, skippedFields, failedFields }
  }

  get insertOrUpdateMode() { return this.__insertOrUpdateMode }

  /**
   * @internal
   * 
   * Revisions document changes made to the document
   * they provide no security or confidentiality 
   */
  addRevision(revision: Revision) {
    this.__revisions.push(revision)
    this.__editVersion = version()
    this.__writtenSinceRevision.clear()
  }

  asReference() {
    return asReference(this)
  }

  /**
   * @internal
   * 
   * This is called for example when the base of the document is updated
   * and no more changes exist
  */
  clearRevisions() {
    this._cancelDeferredRecalculation()
    this.__recalculationPending = false
    this.__revisions = []
    this.__editVersion = version()
    this._clearDirtyTracking()
  }

  get isDirty(): boolean {
    return this.__dirtyFields.size > 0
        || this.__dirtyMeta.locks
        || this.__dirtyMeta.hidden
        || this.__dirtyMeta.readonly
        || this.__dirtyMeta.frozen
        || this.hasRevisions
        || this.__lockChanges.length > 0
        || this.__previousForm != null
  }

  get dirtyFieldCount(): number {
    return this.__dirtyFields.size
  }

  isFieldDirty(fieldName: string): boolean {
    return this.__dirtyFields.has(fieldName)
  }

  getDirtyFields(): {
    name: string,
    label: string,
    section: string,
    before: string,
    after: string,
    beforeValue: { label: string, subtitle: string },
    afterValue: { label: string, subtitle: string },
    reference: boolean,
    beforeReferences: any[],
    afterReferences: any[],
    addedReferences: any[],
    removedReferences: any[],
    reorderedReferences: { id: string, reference: any, fromIndex: number, toIndex: number }[]
  }[] {
    const fieldSections = new Map<string, { label: string, path: string[], index: number, offset: number }>()
    for (const entry of this.__form.getFieldChangeEntries(this.__data)) {
      if (!this.__dirtyFields.has(entry.name)) {
        continue
      }

      fieldSections.set(entry.name, {
        label: entry.label,
        path: entry.sectionPath,
        index: entry.sectionIndex,
        offset: entry.placeholderOffset,
      })
    }

    const fieldOrder = (name: string) => fieldSections.get(name) || { label: name, path: ['Allgemein'], index: Number.MAX_SAFE_INTEGER, offset: Number.MAX_SAFE_INTEGER }

    return Array.from(this.__dirtyFields).filter(name => fieldSections.has(name)).sort((left, right) => {
      const leftOrder = fieldOrder(left)
      const rightOrder = fieldOrder(right)
      return leftOrder.index - rightOrder.index || leftOrder.offset - rightOrder.offset
    }).map(name => {
      const field = this.__form.getField(name) as any
      const currentValue = this.__data[name]
      const baseValue = this.__base.data[name]
      const reference = field?.subType === 'ref'
      const references = (value: any) => {
        if (!value) return []
        return Array.isArray(value) ? value.filter(Boolean) : [value]
      }
      const referenceId = (value: any) => value?.id || ''
      const locale = resolveDirtyFieldLocale()
      const formatBoolean = (value: boolean) => {
        if (locale?.toLowerCase?.().startsWith('de')) {
          return value ? 'Ja' : 'Nein'
        }

        return value ? 'Yes' : 'No'
      }
      const selectOptions = Array.isArray(field?.options) ? field.options : field?.settings?.options
      const formatValue = (value: any): { label: string, subtitle: string } => {
        if (value === undefined) return { label: '-', subtitle: '' }
        if (value === null) return { label: '-', subtitle: '' }
        if (typeof value === 'boolean') return { label: formatBoolean(value), subtitle: '' }
        if (field?.subType === 'select' && Array.isArray(selectOptions)) {
          const values = Array.isArray(value) ? value : [value]
          const parts = values.map(item => {
            const option = selectOptions.find(option => option.value === item)
            const raw = String(item ?? '')
            const display = option?.display ?? raw
            return {
              label: display || '-',
              subtitle: raw && display !== raw ? raw : '',
            }
          })

          return {
            label: parts.map(part => part.label).join(', '),
            subtitle: parts.map(part => part.subtitle).filter(Boolean).join(', '),
          }
        }
        if (typeof value === 'string') return { label: value || '-', subtitle: '' }
        try {
          return { label: JSON.stringify(value), subtitle: '' }
        } catch (error) {
          return { label: String(value), subtitle: '' }
        }
      }
      const beforeValue = formatValue(baseValue)
      const afterValue = formatValue(currentValue)
      const beforeReferences = reference ? references(baseValue) : []
      const afterReferences = reference ? references(currentValue) : []
      const removedReferences = reference ? beforeReferences.filter(beforeReference => {
        const id = referenceId(beforeReference)
        return id && !afterReferences.some(afterReference => referenceId(afterReference) === id)
      }) : []
      const addedReferences = reference ? afterReferences.filter(afterReference => {
        const id = referenceId(afterReference)
        return id && !beforeReferences.some(beforeReference => referenceId(beforeReference) === id)
      }) : []
      const addedReferenceIds = new Set(addedReferences.map(referenceId).filter(Boolean))
      const removedReferenceIds = new Set(removedReferences.map(referenceId).filter(Boolean))
      const reorderedReferences = reference ? afterReferences.map((afterReference, toIndex) => {
        const id = referenceId(afterReference)

        if (!id || addedReferenceIds.has(id) || removedReferenceIds.has(id)) {
          return null
        }

        const fromIndex = beforeReferences.findIndex(beforeReference => referenceId(beforeReference) === id)

        if (fromIndex < 0 || fromIndex === toIndex) {
          return null
        }

        return {
          id,
          reference: afterReference,
          fromIndex,
          toIndex,
        }
      }).filter(Boolean) : []

      return {
        name,
        label: fieldOrder(name).label,
        section: fieldOrder(name).path.join(' / ') || 'Allgemein',
        before: beforeValue.label,
        after: afterValue.label,
        beforeValue,
        afterValue,
        reference,
        beforeReferences,
        afterReferences,
        addedReferences,
        removedReferences,
        reorderedReferences,
      }
    })
  }

  setMutationCallback(callback: (() => void) | null) {
    this.__onMutation = callback
  }

  private _trackFieldDirty(fieldName: string) {
    const currentValue = this.__data[fieldName]
    const baseValue = this.__base.data[fieldName]

    if (fieldValuesEqual(currentValue, baseValue)) {
      this.__dirtyFields.delete(fieldName)
    } else {
      this.__dirtyFields.add(fieldName)
    }
  }

  private _trackLocksDirty() {
    const current = this.__locks
    const base = this.__base.locks

    if (!current && !base) { this.__dirtyMeta.locks = false; return }
    if (!current || !base) { this.__dirtyMeta.locks = true; return }
    if (current.length !== base.length) { this.__dirtyMeta.locks = true; return }

    for (let i = 0; i < current.length; i++) {
      if (current[i].id !== base[i]?.id) { this.__dirtyMeta.locks = true; return }
    }

    this.__dirtyMeta.locks = false
  }

  private _clearDirtyTracking() {
    this.__dirtyFields.clear()
    this.__dirtyMeta.locks = false
    this.__dirtyMeta.hidden = false
    this.__dirtyMeta.readonly = false
    this.__dirtyMeta.frozen = false
  }

  private _notifyMutation() {
    if (this.__onMutation) {
      this.__onMutation()
    }
  }

  private _trackAllFieldsDirty() {
    for (const fieldInfo of this.__form.fields) {
      this._trackFieldDirty(fieldInfo.name)
    }
  }

  private _notifyFieldMutation() {
    this._trackAllFieldsDirty()
    this._scheduleRecalculation()
    this._notifyMutation()
  }

  private _scheduleRecalculation() {
    this.__recalculationPending = true

    if (this.__deferredRecalculationHandle) {
      return
    }

    const run = () => {
      this.__deferredRecalculationHandle = null

      if (this.__recalculationPending) {
        this.recalculateAndValidate()
        this._notifyMutation()
      }
    }

    const global = globalThis as any

    if (typeof global.requestAnimationFrame === 'function') {
      this.__deferredRecalculationHandle = {
        type: 'animationFrame',
        id: global.requestAnimationFrame(run),
      }
    } else if (typeof global.requestIdleCallback === 'function') {
      this.__deferredRecalculationHandle = {
        type: 'idle',
        id: global.requestIdleCallback(run),
      }
    } else {
      this.__deferredRecalculationHandle = {
        type: 'timeout',
        id: setTimeout(run, 0),
      }
    }
  }

  private _cancelDeferredRecalculation() {
    if (!this.__deferredRecalculationHandle) {
      return
    }

    const { type, id } = this.__deferredRecalculationHandle
    const global = globalThis as any

    if (type === 'animationFrame' && typeof global.cancelAnimationFrame === 'function') {
      global.cancelAnimationFrame(id)
    } else if (type === 'idle' && typeof global.cancelIdleCallback === 'function') {
      global.cancelIdleCallback(id)
    } else if (type === 'timeout') {
      clearTimeout(id)
    }

    this.__deferredRecalculationHandle = null
  }

  get lastRevisionType(): RevisionType {
    if (this.__revisions.length === 0) {
      return 'user'
    } else {
      return this.__revisions[this.__revisions.length - 1].type
    }
  }

  recalc() {
    this._cancelDeferredRecalculation()
    this.__recalculationPending = false
    this.__form.recalculateData(this.__data)

    this.__validationErrors = this.__form.validateDocumentData(this.__data)
    this._trackAllFieldsDirty()

    return this
  }

  commit() {
    return this.recalc()
  }

  recalculateAndValidate() {
    this.recalc()
  }

  private _initializeValidationErrors(document: Types.Document, dataToUse?: any) {
    if (!dataToUse && document.valid === true) {
      this.__form.recalculateData(this.__data)
      this.__validationErrors = null
      return
    }

    this.recalculateAndValidate()
  }

  // Note: `recalculateAndValidate` needs to be called before
  isRequiredField(fieldName: string): boolean {
    return !!this.getRequiredFieldErrorInfo(fieldName)
  }

  getValidationErrors() {
    return this.__validationErrors
  }

  getRequiredFieldErrorInfo(fieldName: string): RequiredField {
    return this.__validationErrors?.[fieldName]?.required
  }

  getValidationErrorsForField(fieldName: string) {
    return this.__validationErrors?.[fieldName]
  }

  /** @internal */
  updateBase(document: Types.Document, form: Form, dataToUse?: any) {
    this._cancelDeferredRecalculation()
    this.__recalculationPending = false
    this.__base = document
    this.__form = form
    this.__previousForm = null
    this.__formRefOverride = null
    this.__previousRef = null

    this.__cloneOf = cloneReference(document.cloneOf)
    this.__originalCloneOf = cloneReference(document.originalCloneOf)

    this.__locks = cloneReferences(document.locks)
    this.__data = form.cloneDocumentData(dataToUse || document.data)
    this.__dataProxy = this._buildProxy(this.__data)
    this._clearDirtyTracking()

    this.__hidden = document.hidden === true
    this.__readonly = document.readonly === true
    this.__frozen = document.frozen || null

    this._initializeValidationErrors(document, dataToUse)

    // After merging, re-evaluate which fields still differ from the new base.
    this._trackAllFieldsDirty()
    this._trackLocksDirty()
    this.__dirtyMeta.hidden = (this.__hidden !== (document.hidden === true))
    this.__dirtyMeta.readonly = (this.__readonly !== (document.readonly === true))
    this.__dirtyMeta.frozen = (!!this.__frozen !== !!document.frozen)

    // If nothing differs, clear leftover revisions too
    if (!this.isDirty) {
      this.__revisions = []
      this.__editVersion = version()
      this.__lockChanges = []
      this.__changeOperations = []
      this.__writtenSinceRevision = new Set()
    }
  }

  /**
   * Revert data, locks, hidden, and readonly back to the base document state.
   * Clears all revisions and dirty flags.
   */
  revertToBase() {
    this._cancelDeferredRecalculation()
    this.__recalculationPending = false

    if (this.__previousForm) {
      this.__form = this.__previousForm
      this.__previousForm = null
      this.__formRefOverride = null
      this.__previousRef = null
    }

    this.__data = this.__form.cloneDocumentData(this.__base.data)
    this.__dataProxy = this._buildProxy(this.__data)
    this.__locks = cloneReferences(this.__base.locks)
    this.__lockChanges = []
    this.__hidden = this.__base.hidden === true
    this.__readonly = this.__base.readonly === true
    this.__frozen = this.__base.frozen || null
    this.__revisions = []
    this.__editVersion = version()
    this._clearDirtyTracking()
    this.__changeOperations = []
    this.__writtenSinceRevision = new Set()
    this.__pendingFileUploadFields = null
    this._initializeValidationErrors(this.__base)
  }

  disableProxy() {
    this.__dataProxy = null
  }

  serializeForPostMessage() {
    let proxy = this.__dataProxy
    this.__dataProxy = null

    try {
      return JSON.stringify(this)
    } finally {
      this.__dataProxy = proxy
    }
  }

  public static isSerializedForPostMessage(value: object) {
    if (value && '__base' in value && '__data' in value && '__form' in value) {
        return true
      } else {
        return false
      }
  }

  private _buildProxy(data: any) {
    // Build a proxy that will intercept all field accesses and writes.
    // We will pass through all fields that are part of the form and
    // all properties that start with '__v_' (for vue compatibility)

    const handler = {
      set: (target: any, property: string, value: any) => {
        if (!property) {
          return
        }

        if (isDynamicVueProperty(property) || isSymbol(property)) {
          target[property] = value
          return
        }

        if (!this.__form.hasField(property)) {
          console.error(`Trying to write to unknown field ${property} on document ${this.id}@${this.v}`)
          throw new Error(`Unknown field ${property}`)
        }

        const field = this.__form.getField(property)
        value = normalizeFieldValueForAssignment(field, value, this.__form.typeValidation)

        // Allow undefined/null assignments without validation
        if (value === undefined || value === null) {
          this.__writtenSinceRevision.add(property)
          target[property] = value
          this._trackFieldDirty(property)
          this._notifyFieldMutation()
          return true
        }

        // Validate type — throw on mismatch
        if (!this.__form.validateFieldValue(property, value)) {
          const errorMessage = formatInvalidFieldValueMessage(field, value, this.id, String(this.v), this.__form.id, String(this.__form.v))
          throw new Error(errorMessage)
        }

        this.__writtenSinceRevision.add(property)
        target[property] = value
        this._trackFieldDirty(property)
        this._notifyFieldMutation()

        return true
      },
      get: (target: any, property: string) => {
        if (!property) {
          return 
        }
        
        if (isDynamicVueProperty(property) || isSymbol(property)) {
          return this.__base[property]
        }

        if (!this.__form.hasField(property) && Object.prototype.hasOwnProperty.call(target, property)) {
          console.warn(`Accessing unknown field ${property} on document ${this.id}@${this.v}`)
        }

        return target[property]
      }
    }

    return new Proxy(data, handler);
  }

  get form() {
    return this.__form
  }

  get id() {return this.__base.id}
  get v() {return this.__base.v}
  get valid() {return this.__validationErrors == null}
  set valid(value: boolean) {
    if (value === true) {
      this.__validationErrors = null
      return
    }

    this.recalculateAndValidate()
  }
  get previous() {return this.__previousRef ?? this.__base.previous}
  get p() {return this.__base.p}
  get f() {return this.__formRefOverride ?? this.__base.f}
  get cloneOf() {return this.__cloneOf}  
  get originalCloneOf() {return this.__originalCloneOf}  
  get meta() {return this.__base.meta }
  
  /**
   * Previous document ONLY includes the changes from the previous saved version to the `base` of this `EditableDocument`. It
   * does NOT include currently made changes to this instance of `EditableDocument`
   */
  
  get data() {
    return this.__dataProxy ?? this.__data
  }

  set data(data: any) {
    this.setData(data, { cloneFromDefault: true })
  }

  private _throwIfReadonly() {
    if (this.isFrozen) {
      throw new Error('Cannot change document when frozen')
    }

    if (this.__readonly) {
      throw new Error('Cannot change document when readonly')
    }
  }

  isUndefinedField(fieldName: string) {
    return this.__form.isUndefinedField(this, fieldName)
  }

  getUndefinedFields(sectionIndex?: number) {
    return this.__form.getUndefinedFields(this, sectionIndex)
  }

  getUndefinedSections() {
    return this.__form.getUndefinedSections(this)
  }

  isTaggedField(fieldName: string, matcher: TagMatch[], options?: TagMatchOptions) {
    return this.__form.isTaggedField(fieldName, matcher, options)
  }

  getTaggedFields(matcher: TagMatch[], sectionIndex?: number): string[]
  getTaggedFields(matcher: TagMatch[], options?: TagMatchOptions): string[]
  getTaggedFields(matcher: TagMatch[], sectionIndexOrOptions?: number|TagMatchOptions, options?: TagMatchOptions): string[]
  getTaggedFields(...args: any[]): string[] {
    return this.__form.getTaggedFields.call(this.__form, ...args) 
  }

  getTaggedSections(matcher: TagMatch[], options?: TagMatchOptions) {
    return this.__form.getTaggedSections(matcher, options)
  }

  setSectionToUndefined(sectionIndex: number) {
    for (const field of this.form.enumerateAllFieldsInSection(sectionIndex)) {
      const isUndefinedField = this.data[field.name] === undefined

      if (!isUndefinedField) {
        this.data[field.name] = undefined
      }
    }
  }

  resetFieldToDefaults(fieldName: string) {
    const field = this.form.getField(fieldName)

    if (!field) {
      throw new Error(`Field ${fieldName} does not exist`)
    }

    this.data[fieldName] = this.form.cloneFieldValue(undefined, fieldName, true)
  }

  resetSectionToDefaults(sectionIndex: number) {
    for (const field of this.form.enumerateAllFieldsInSection(sectionIndex)) {
      const isUndefinedField = this.data[field.name] === undefined

      if (isUndefinedField) {
        this.data[field.name] = this.form.cloneFieldValue(undefined, field.name, true)
      }
    }
  }

  setField(fieldName: string, newValue: any, { ignoreReadonly = false }: { ignoreReadonly?: boolean } = {}) {
    if (!ignoreReadonly) {
      this._throwIfReadonly()
    }

    const field = this.__form.getField(fieldName)

    if (!field) {
      throw new Error(`Field ${fieldName} does not exist`)
    }

    newValue = normalizeFieldValueForAssignment(field, newValue, this.__form.typeValidation)

    if (!this.__form.validateFieldValue(fieldName, newValue)) {
      throw new Error(`Invalid value for field ${fieldName}`)
    }

    this.__writtenSinceRevision.add(fieldName)
    this.__data[fieldName] = this.__form.cloneFieldValue(newValue, fieldName, true)
    this._trackFieldDirty(fieldName)
    this._notifyFieldMutation()
  }

  setData(newData: any, options: {cloneFromDefault?: boolean, excludeFileFields?: boolean, onlyMatchingFields?: boolean, ignoreReadonly?: boolean} = {}) {
    if (options.ignoreReadonly !== true) {
      this._throwIfReadonly()
    }

    const cloneFromDefault = options.cloneFromDefault
    const excludeFileFields = options.excludeFileFields
    const onlyMatchingFields = options.onlyMatchingFields

    const data = this.data

    for (const fieldInfo of this.__form.fields) {
      if ((fieldInfo.subType === 'file' || fieldInfo.subType === 'image') && excludeFileFields) {
        continue
      }

      if (onlyMatchingFields) {
        if (!(fieldInfo.name in newData)) {
          continue
        }
      }

      const fieldName = fieldInfo.name
      let value = newData[fieldName]

      if (value && Form.isRefField(fieldInfo)) {
        if (fieldInfo.cardinality) {
          if (Array.isArray(value)) {
            value = value.map(ref => fixOldStyleReference(ref))
          }
        } else {
          value = fixOldStyleReference(value)
        }
      }

      value = normalizeFieldValueForAssignment(fieldInfo, value, this.__form.typeValidation)

      if (typeof value === 'undefined') {
        if (this.form.allowsUndefinedFields) {
          if (cloneFromDefault) {
            throw new Error('Cannot set a field to `undefined` when the form allows undefined fields but cloneFromDefault is true. The behavior is duplicitous in this case. Should it clone from default or assign undefined')
          }
          this.__writtenSinceRevision.add(fieldName)
          this.__data[fieldName] = undefined
        } else {
          if (!cloneFromDefault) {
            throw new Error(`New data is missing field ${fieldName} which is not allowed by the form, document: ${this.id}@${this.v}, form: ${this.__form.id}@${this.__form.v}`)
          }
          this.__writtenSinceRevision.add(fieldName)
          this.__data[fieldName] = this.__form.cloneFieldValue(value, fieldName, cloneFromDefault)
        }
      } else {
        const validationResult = this._validateFieldValue(fieldName, value)

        if (!validationResult.valid) {
          throw new Error(formatInvalidFieldValueMessage(fieldInfo, value, this.id, this.v, this.__form.id, this.__form.v, validationResult.error))
        }

        this.__writtenSinceRevision.add(fieldName)
        this.__data[fieldName] = this.__form.cloneFieldValue(value, fieldName, cloneFromDefault)
      }
    }

    this._notifyFieldMutation()
  }

  private _validateFieldValue(fieldName: string, value: any): { valid: boolean, error?: unknown } {
    try {
      return { valid: this.__form.validateFieldValue(fieldName, value) }
    } catch (error) {
      return { valid: false, error }
    }
  }

  increment(fieldName: string, valueToIncreaseBy: number = 1): number  {
    if (valueToIncreaseBy < 0) {
      throw new Error('Invalid argument exception. Can only increase not decrease')
    }

    return this.changeNumberBy(fieldName, valueToIncreaseBy)
  }

  decrement(fieldName: string, valueToDecreaseBy: number = 1): number {
    if (valueToDecreaseBy < 0) {
      throw new Error('Invalid argument exception. Can only decrease not increase')
    }

    return this.changeNumberBy(fieldName, -valueToDecreaseBy)
  }

  changeNumberBy(fieldName: string, valueToIncreaseBy: number = 1): number  {
    this._throwIfReadonly()

    if (valueToIncreaseBy < 0.0000001 && valueToIncreaseBy > -0.0000001) {
      return
    }

    const fieldDefinition = this.form.getField(fieldName)

    if (!fieldDefinition) {
      throw new Error(`Field ${fieldName} is unknown`)
    }

    if (fieldDefinition.type !== 'number') {
      throw new Error('Can only increment/decrement numeric fields')
    }

    if (fieldDefinition.cardinality) {
      throw new Error('Can only apply increment/decrement to single cardinality fields')
    }

    if (!this.form.allowsUndefinedFields && this.__data[fieldName] === undefined) {
      throw new Error('Cannot increment/decrement field. It is undefined and the form does not allow undefined fields')
    }

    this.__changeOperations.push({
      type: 'increment',
      field: fieldName,
      delta: valueToIncreaseBy
    })

    const newValue = (this.__data[fieldName] ?? 0) as number + valueToIncreaseBy

    this.__data[fieldName] = newValue

    this._trackFieldDirty(fieldName)
    this._notifyFieldMutation()

    return newValue

    // NOTE: When proxies are used in the future for field accesses, subsequent
    // access via direct assignment should be forbidden
  }

  get locks(): Types.Reference[] {return this.__locks}
  set locks(locks: Types.Reference[]) {
    for (const l of locks) {
      if (!l.id || !l.v || l.text == null) {
        console.error('Attempt to set invalid locks', locks)
        throw new Error('Cannot set empty lock references (or references without version or null text. You cannot set documents directly)')
      }
    }
    
    const newLocks: Types.Reference[] = []
    for (const l of locks) {
      newLocks.push( {id: l.id, v: l.v, text: l.text })
    }

    const lockChanges = {
      added: cloneReferences(newLocks),
      updated: [],
      removed: [],
    }
    if (this.__locks) {
      lockChanges.removed = cloneReferences(this.__locks)
    }
    this.__lockChanges.push(lockChanges)

    this.__locks = newLocks
    this._trackLocksDirty()
    this._notifyMutation()
  }

  get stampData() {
    return this.__base.stampData
  }

  get stamps() {
    return this.__base.stamps
  }
  
  get t() {return this.__base.t}
  get ct() {return this.__base.ct}
  
  // The following properties should not be written to. Theoretically a user of the
  // api could get a document and alter these properties (e.g. push onto 'a') but
  // this is not a supported mode of interaction.
  //
  // Should this become an issue we will clone those fields too
  get created() {return this.__base.created}
  get modified() {return this.__base.modified}
 
  get a_depth() {return this.__base.a_depth}
  get a() {return this.__base.a}

  get a_forms() {return this.__base.a_forms}
  get inheritedLocks() {return this.__base.inheritedLocks}

  addLock(lock: Types.Document, options: AddLockOptions = null): boolean {
    if (!lock || !lock.f || lock.f.id !== 'lock-form') {
      throw new Error('Cannot only add reference to an actual lock document')
    }

    const opts = {...options}

    for (const existingLock of this.__locks) {
      if (existingLock.id === lock.id) {
        this.__lockChanges.push({
          added: [],
          updated: [lock],
          removed: []
        })

        const result = updateReference(existingLock, lock, opts.updateVersionIfAlreadyKnown)
        this._trackLocksDirty()
        this._notifyMutation()
        return result
      }
    }

    const newLockRef: Types.Reference = {
      id: lock.id,
      v: lock.v,
      text: <string>lock.data.name
    }

    this.__locks.push(newLockRef)
    this.__lockChanges.push({
      added: [newLockRef],
      updated: [],
      removed: []
    })
    this._trackLocksDirty()
    this._notifyMutation()
  }

  removeLock(lock: string|Types.Document|Types.Reference) {
    const id = typeof lock === 'string' ? lock : lock.id

    for (let i = this.__locks.length - 1; i >= 0; i -= 1) {
      const existingLock = this.__locks[i]

      if (existingLock.id === id) {
        const removedLocks = this.__locks.splice(i, 1)

        this.__lockChanges.push({
          added: [],
          updated: [],
          removed: removedLocks
        })

        this._trackLocksDirty()
        this._notifyMutation()
        return true
      }
    }

    return false
  }

  hasLock(lock?: Types.Document| Types.Reference) {
    if (!lock) {
      return (this.__locks.length > 0)
    }

    for (const existingLock of this.__locks) {
      if (existingLock.id === lock.id) {
        return true
      }
    }

    return false
  }

  hasInheritedLock(lock ?: Types.Document | Types.Reference) {
    if (!lock) {
      if (!this.__base.inheritedLocks) {
        return false
      }

      for (const locks of this.__base.inheritedLocks) {
        if (locks && locks.length > 0) {
          return true
        }
      }

      return false
    }

    for (const existingLocks of this.__base.inheritedLocks) {
      for (const existingLock of existingLocks) {
        if (existingLock.id === lock.id) {
          return true
        }
      }
    }

    return false
  }

  /** @internal */
  get lockChanges() {
    return this.__lockChanges
  }
  
  /** @internal */
  clearLockChanges() {
    this.__lockChanges = []
  }

  /** @internal */
  get ChangeOperations() {
    return this.__changeOperations
  }

  /** @internal */
  clearChangeOperations() {
    this.__changeOperations = []
  }

  /**
   * Reference helpers
   */

  // TODO: also allow just adding Ref
  addReference(fieldName: string, document: Types.Document, options: AddReferenceOptions = null): boolean {
    this._throwIfReadonly()

    const opts = { ...DEFAULT_ADDREFERENCE_OPTIONS, ...options }

    if (!fieldName) {
      throw new Error(`addReference: Missing param fieldName on Document ${this.toLogDocumentInfo()}`)
    }

    if (!document) {
      throw new Error(`addReference: Missing param document on Document ${this.toLogDocumentInfo()}`)
    }

    const field = this.__form.getField(fieldName)

    if (!field) {
      throw new Error(`Field ${fieldName} does not exist on Document ${this.toLogDocumentInfo()}`)
    }
    
    if (!Form.isRefField(field)) {
      throw new Error(`Cannot add reference to non reference field ${fieldName} on Document ${this.toLogDocumentInfo()}`)
    }

    if (!field.cardinality) {
      const existingReference = this.data[fieldName]

      if (existingReference) {
        const result = updateReference(existingReference, document, opts.updateVersionIfAlreadyKnown)
        this._trackFieldDirty(fieldName)
        this._notifyFieldMutation()
        return result
      } else {
        this.__data[fieldName] = {
          id: document.id,
          v: document.v,
          text: document.data.name
        }
      }
    } else {
      let existingReferences = this.data[fieldName] 
      
      if (!existingReferences) {
        existingReferences = []
        this.__data[fieldName] = existingReferences
      }

      if (opts.allowMultiple) {
        const newReference = {
          id: document.id,
          v: document.v,
          text: document.data.name
        }

        if (opts.addToBeginning) {
          existingReferences.unshift(newReference)
        } else {
          existingReferences.push(newReference)
        }
      } else {
        const existingReference = existingReferences.filter((ref) => ref.id === document.id)[0]

        if (existingReference) {
          const result = updateReference(existingReference, document, opts.updateVersionIfAlreadyKnown)
          this._trackFieldDirty(fieldName)
          this._notifyFieldMutation()
          return result
        } else {
          const newReference = {
            id: document.id,
            v: document.v,
            text: document.data.name
          }

          if (opts.addToBeginning) {
            existingReferences.unshift(newReference)
          } else {
            existingReferences.push(newReference)
          }
        }
      }
    }

    this._trackFieldDirty(fieldName)
    this._notifyFieldMutation()
  }

  removeReference(fieldName: string, documentIdentifier ?: Types.IdOrReference): boolean {
    this._throwIfReadonly()

    if (!fieldName) {
      throw new Error(`removeReference: Missing param fieldName on Document ${this.toLogDocumentInfo()}`)
    }

    let documentId: string
    
    if (documentIdentifier) {
      documentId = typeof documentIdentifier === 'string' ? documentIdentifier : documentIdentifier.id
    }

    const field = this.__form.getField(fieldName)

    if (!field) {
      throw new Error(`Field ${fieldName} does not exist on Form on Document ${this.toLogDocumentInfo()}`)
    }
    
    if (!Form.isRefField(field)) {
      throw new Error(`Cannot remove reference from non reference field ${fieldName} on Document ${this.toLogDocumentInfo()}`)
    }

    if (!field.cardinality) {
      let existingReference = this.data[fieldName]

      if (!existingReference) {
        existingReference = { id: '', v: '', text: '' }
        this.__data[fieldName] = existingReference
      }

      if (!documentId || existingReference.id === documentId) {
        existingReference.id = ''
        existingReference.v = ''
        existingReference.text = ''
      } else {
        return false
      }
    } else {
      let existingReferences: Types.Reference[] = this.data[fieldName] 
      
      if (!existingReferences) {
        existingReferences = []
        this.__data[fieldName] = existingReferences
      }
      
      for (let i = 0; i < existingReferences.length; i += 1) {
        const existingReference = existingReferences[i]

        if (existingReference.id === documentId) {
          existingReferences.splice(i, 1)
          this._trackFieldDirty(fieldName)
          this._notifyFieldMutation()
          return true
        }
      }

      return  false
    }

    this._trackFieldDirty(fieldName)
    this._notifyFieldMutation()
    return true
  }

  isReferencing(fieldName: string = null, documentIdentifier: Types.IdOrReference, options?: { strict?: boolean } ) {
    if (fieldName) {
      const field = this.data[fieldName]
      return isFieldReferencing(field, documentIdentifier, options)
    } else {
      for (const { name } of this.__form.fields) {
        const field = this.data[fieldName]
        return isFieldReferencing(field, documentIdentifier, options)
      }
    }
  }
  
  /**
   * Set a file field to the desired content
   * 
   * In case of multi file fields it resets it to an array with 
   * only one value.
   * 
   * If the specified value is null or undefined the field is
   * reset to empty. So either field:null or field:[] depending
   * on cardinality 
   * 
   * @param fieldName   [string] Name of the field (must be of type File/Image)
   * @param content     [string|Node.js Buffer|File|Blob]  
   * @param options     Options
   */
  setFile(fieldName: string, content: string|Buffer | File | Blob | ArrayBuffer | NodeJS.ReadableStream, options ?: FileSetOptions) {
    this._throwIfReadonly()

    if (!this.__createFileURL) {
      throw new Error('Cannot set file since createFileURL option not configured')
    }

    const fieldInfo = this.__form.getField(fieldName)

    if (!fieldInfo) {
      throw new Error('Unknown field')
    }

    if (!Form.isFileField(fieldInfo)) {
      throw new Error(`Field ${fieldName} must be a File/Image field but is ${fieldInfo.type}/${fieldInfo.subType}`)
    }

    if (Form.isMultiField(fieldInfo)) {
      throw new Error(`File fields with cardinality > 1 are not supported ${this.__form.id}@${this.__form.v} [${fieldName}] = ${JSON.stringify(fieldInfo, null, 2)}`)
    }

    if (!content) {
      if (Form.isMultiField(fieldInfo)) {
        this.__data[fieldName] = []
      } else {
        this.__data[fieldName] = null
      }

      this._trackFieldDirty(fieldName)
      this._notifyFieldMutation()
      return
    }

    const fileFieldValue: FileField = {
      name: null,
      size: null,
      type: null,
      sha1: null,
      dataUrl: null,
      thumbnailUrl: null,
      iconUrl: null,
      previewUrl: null,
      fileInfo: {
        created: null,
        read: null,
        modified: null,
        imported: new Date().valueOf(),
      }
    }           
              
    if (fieldInfo.subType === 'image') {
      fileFieldValue.mediaInfo = {
        width: null,
        height: null
      }
    }

    if (typeof content === 'string') {
      this._readDataUrlIntoFileField(fileFieldValue, content)
    } else if (typeof Buffer !== 'undefined' && Buffer.isBuffer(content)) {
      this._readBufferIntoFileField(fileFieldValue, content)
    } else if (typeof File !== 'undefined' && content instanceof File) {
      this._readFileIntoFileField(fileFieldValue, content)
    } else if (typeof Blob !== 'undefined' && content instanceof Blob) {
      this._readBlobIntoFileField(fileFieldValue, content)
    } else if (content && (<NodeJS.ReadableStream>content).pipe) {
      this._readStreamIntoFileField(fileFieldValue, <NodeJS.ReadableStream>content)
    } else {
      throw new Error('Unsupported content type')
    }

    if (options) {
      if (options.name) {
        fileFieldValue.name = options.name
      }

      if (options.type) {
        fileFieldValue.type = options.type
      }
    }

    if (this.__pendingFileUploadFields === null) {
      this.__pendingFileUploadFields = new Set()
    }

    this.__pendingFileUploadFields.add(fieldName)
    this.__data[fieldName] = fileFieldValue
    this._trackFieldDirty(fieldName)
    this._notifyFieldMutation()
  }
  
  /** @internal */
  public retrieveUploads() {
    if (!this.__pendingFileUploadFields) {
      return null
    }
    
    const fieldNames = Array.from(this.__pendingFileUploadFields.keys())
    this.__pendingFileUploadFields = null
    return fieldNames
  }

  private _readBufferIntoFileField(fileField: FileField, content: Buffer) {
    fileField.type = 'application/octet-stream'
    fileField.size = content.byteLength
    fileField.dataUrl = this.__createFileURL(<any>content)
  }

  private _readBlobIntoFileField(fileField: FileField, content: Blob) {
    fileField.type = content.type
    fileField.size = content.size
    fileField.dataUrl = this.__createFileURL(content)
  }

  private _readFileIntoFileField(fileField: FileField, content: File) {
    this._readBlobIntoFileField(fileField, content)
    fileField.name = content.name
    fileField.fileInfo.modified = content.lastModified && content.lastModified.valueOf ? content.lastModified.valueOf() : null
  }

  private _readStreamIntoFileField(fileField: FileField, content: NodeJS.ReadableStream) {
    fileField.dataUrl = this.__createFileURL(<any>content)
  }

  private _readDataUrlIntoFileField(fileField: FileField, dataUrl: string) {
    const protocol = dataUrl.slice(0, 5)
    
    if (protocol !== 'data:') {
      throw new Error('text must be a data-uri to be writeable into file field')
    }  

    const indexOfFirstCommaAndThusBeginningOfEncodedData = dataUrl.indexOf(',', 5)

    if (indexOfFirstCommaAndThusBeginningOfEncodedData > 5) {
      const textBetweenColonAndComma = dataUrl.substring(5, indexOfFirstCommaAndThusBeginningOfEncodedData)
      const [mediatype, encodingToken] = textBetweenColonAndComma.split(';', 2)

      if (encodingToken && encodingToken !== 'base64') {
        throw new Error('Invalid data-uri. data:[<mediatype>][;base64|charset=<US-ASCII>],<data>')
      }

      fileField.type = mediatype || DEFAULT_MIME_TYPE
    } else {
      fileField.type = DEFAULT_MIME_TYPE
    }

    fileField.dataUrl = this.__createFileURL(<any>dataUrl)
  }

  private toLogDocumentInfo() {
    return `${this.id}@${this.v} with Form ${this.__form.id}@${this.__form.v}`
  }
}

function updateReference(reference, targetDocument, updateVersionIfAlreadyKnown) {
  if (reference.id === targetDocument.id) {
    if (!updateVersionIfAlreadyKnown) {
      return false
    }
  } else {
    reference.id = targetDocument.id
  }

  reference.v = targetDocument.v
  reference.text = targetDocument.data.name

  return true
}

function cloneReferences(refs: Types.Reference[]): Types.Reference[] {
  if (refs == null || refs.length === 0) {
    return []
  } else {
    return refs.map(cloneReference)
  }
}

function cloneReference(ref: Types.Reference): Types.Reference {
  if (ref == null) {
    return ref
  } else {
    return { id: ref.id, v: ref.v, text: ref.text}
  }
}

function fixOldStyleReference(ref) {
  if (!ref) {
    return ref
  }

  if (isEmptyObject(ref)) {
    return { id: '', v: '', text: '' }
  } else if (ref.id === '' && ref.v === 0 && !ref.text) {
    return { id: '', v: '', text: '' }
  } else if (typeof ref.v === 'number') {
    return { id: ref.id, v: 'converted', text: ref.text }
  } else {
    return ref
  }
}

function isEmptyObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0
}

function normalizeFieldValueForAssignment(fieldInfo: FieldInfo, value: any, typeValidation?: Required<TypeValidationOptions>) {
  if (!fieldInfo) {
    return value
  }

  if (fieldInfo.cardinality) {
    if (!Array.isArray(value)) {
      return value
    }

    return value.map(item => normalizeSingleFieldValueForAssignment(fieldInfo, item, typeValidation))
  }

  return normalizeSingleFieldValueForAssignment(fieldInfo, value, typeValidation)
}

function coerceFieldCardinality(fieldInfo: FieldInfo, value: any) {
  if (fieldInfo.cardinality) {
    return Array.isArray(value) ? value : [value]
  }

  return Array.isArray(value) ? value[0] : value
}

function normalizeSingleFieldValueForAssignment(fieldInfo: FieldInfo, value: any, typeValidation?: Required<TypeValidationOptions>) {
  if (value == null) {
    return value
  }

  if (DateTime.isDateTime(value)) {
    if (fieldInfo.type === 'string') {
      return normalizeDateTimeForStringField(fieldInfo, value, typeValidation)
    }

    if (fieldInfo.type === 'number') {
      return normalizeDateTimeForNumberField(fieldInfo, value)
    }
  }

  if (fieldInfo.type === 'object' && fieldInfo.subType === 'duration' && DateTimeDuration.isDuration(value)) {
    return value.toObject()
  }

  if (fieldInfo.type === 'object' && fieldInfo.subType === 'currency') {
    return normalizeCurrencyFieldValueForAssignment(fieldInfo, value)
  }

  if (fieldInfo.type === 'object' && (fieldInfo.subType === 'file' || fieldInfo.subType === 'image')) {
    return normalizeFileFieldValueForAssignment(value)
  }

  if (
    fieldInfo.type === 'string' &&
    fieldInfo.subType === 'date' &&
    typeValidation?.strictTemporalTypes === false &&
    typeValidation.dateTimeToDateFieldMode === 'strip'
  ) {
    return extractDatePartFromDateTimeString(value) ?? value
  }

  return value
}

function normalizeCurrencyFieldValueForAssignment(fieldInfo: FieldInfo, value: any) {
  if (!value || typeof value !== 'object' || Array.isArray(value) || typeof value.amount !== 'number') {
    return value
  }

  const normalizedAmount = normalizeCurrencyBaseUnitAmount(value.amount, {
    context: `field ${fieldInfo.name}`,
  })

  if (Object.is(normalizedAmount, value.amount)) {
    return value
  }

  return {
    ...value,
    amount: normalizedAmount,
  }
}

function normalizeFileFieldValueForAssignment(value: any) {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return value
  }

  let didNormalize = false
  const normalizedValue = { ...value }

  if (normalizedValue.dataUrl === null) {
    normalizedValue.dataUrl = ''
    didNormalize = true
  }

  const fileInfo = value.fileInfo

  if (fileInfo == null || typeof fileInfo !== 'object' || Array.isArray(fileInfo)) {
    return didNormalize ? normalizedValue : value
  }

  const normalizedFileInfo = { ...fileInfo }

  for (const field of FILE_INFO_TIMESTAMP_FIELDS) {
    if (!(field in normalizedFileInfo) || normalizedFileInfo[field] === undefined) {
      normalizedFileInfo[field] = null
      didNormalize = true
    }
  }

  if (!didNormalize) {
    return value
  }

  return { ...normalizedValue, fileInfo: normalizedFileInfo }
}

// The instant in the instance's zone, with that zone (`…+02:00[Europe/Berlin]`).
// Without an instance zone the value keeps its own offset and has no zone.
function toInstantInInstanceZone(value: DateTime, timezone: string | null | undefined) {
  if (!timezone) {
    return value.toISO({ includeOffset: true })
  }

  return value.setZone(timezone).toISO({ includeOffset: true, extendedZone: true })
}

function normalizeDateTimeForStringField(fieldInfo: FieldInfo, value: DateTime, typeValidation?: Required<TypeValidationOptions>) {
  if (!value.isValid) {
    return value
  }

  switch (fieldInfo.subType) {
    case 'datetime':
      return toInstantInInstanceZone(value, typeValidation?.timezone)
    case 'date':
      // Non-strict date fields hold full date-times; strict ones (and the
      // `strip` mode) only the calendar date of the value as it is.
      if (typeValidation?.strictTemporalTypes === false && typeValidation.dateTimeToDateFieldMode !== 'strip') {
        return toInstantInInstanceZone(value, typeValidation.timezone)
      }
      return value.toISODate()
    case 'time':
      return value.toISOTime({ includeOffset: true })
    case 'dayofyear':
      return value.toFormat("'--'MM-dd")
    case 'monthofyear':
      return value.toFormat("yyyy-MM")
    default:
      return value
  }
}

function normalizeDateTimeForNumberField(fieldInfo: FieldInfo, value: DateTime) {
  if (!value.isValid) {
    return value
  }

  switch (fieldInfo.subType) {
    case 'month':
      return value.month
    case 'year':
      return value.year
    default:
      return value
  }
}

function formatInvalidFieldValueMessage(fieldInfo, value, documentId: string, documentVersion: string, formId: string, formVersion: string, validationError?: unknown) {
  const parts = [
    `Invalid value for field ${fieldInfo.name} in document ${documentId}@${documentVersion} form ${formId}@${formVersion}`,
    `expected ${formatExpectedFieldValue(fieldInfo)}`,
    `received ${formatValueType(value)} ${formatValueForError(value)}`
  ]

  if (validationError) {
    parts.push(`validator error ${formatValueForError(validationError instanceof Error ? validationError.message : validationError)}`)
  }

  return parts.join('; ')
}

function formatExpectedFieldValue(fieldInfo) {
  const cardinality = fieldInfo.cardinality ? `[] ${fieldInfo.cardinality.min}-${fieldInfo.cardinality.max}` : ''
  const subtype = fieldInfo.subType ? `/${fieldInfo.subType}` : ''
  return `${fieldInfo.type}${subtype}${cardinality}`
}

function formatValueType(value) {
  if (Array.isArray(value)) {
    return 'array'
  }

  if (value === null) {
    return 'null'
  }

  return typeof value
}

function formatValueForError(value) {
  let serialized: string

  try {
    serialized = JSON.stringify(value)
  } catch (error) {
    serialized = String(value)
  }

  if (serialized === undefined) {
    serialized = String(value)
  }

  if (serialized.length > MAX_ERROR_VALUE_LENGTH) {
    return `${serialized.substring(0, MAX_ERROR_VALUE_LENGTH)}...`
  }

  return serialized
}

function isFieldReferencing(refField, documentIdentifier: Types.IdOrReference, options?: { strict?: boolean }) {
  if (refField && Array.isArray(refField)) {
    for (const ref of refField) {
      if (isReferenceTo(ref, documentIdentifier, options)) {
        return true
      }
    }
    return false
  } else {
    return isReferenceTo(refField, documentIdentifier, options)
  }
}

function isReferenceTo(ref: Types.Reference, documentIdentifier: Types.IdOrReference, options?: { strict?: boolean }) {
  const { id, v } = typeof documentIdentifier === 'string' ? { id: documentIdentifier, v: null } : documentIdentifier

  if (ref && ref.id === id) {
    if (options && options.strict && v) {
      return (ref.v === v)
    } else {
      return true
    }
  }

  return false
}

function isSymbol(value: any) {
  return typeof value === 'symbol' || (typeof value === 'object' && Object.prototype.toString.call(value) === '[object Symbol]')
}

function isDynamicVueProperty(property: any) {
  if (typeof property !== 'string') {
    return false
  }

  return property.startsWith('__v_') || property == '__ob__'
}

function fieldValuesEqual(a: any, b: any): boolean {
  if (a === b) return true
  if (a == null && b == null) return true
  if (a == null || b == null) return false

  if (typeof a !== typeof b) return false

  if (typeof a !== 'object') return false

  try {
    return jsonValuesEqual(JSON.parse(JSON.stringify(a)), JSON.parse(JSON.stringify(b)))
  } catch (e) {
    return false
  }
}

/**
 * Structural equality of JSON values. Object key order is irrelevant: stored documents and
 * controls may build the same value with keys in a different order. Array order matters.
 * A key that is missing and a key that is null are the same: copying a value for editing fills
 * in null for properties a stored value doesn't have (a file's `previewUrl`, say), which is no
 * change.
 */
function jsonValuesEqual(a: any, b: any): boolean {
  if (a === b) return true
  if (a === null || b === null || typeof a !== 'object' || typeof b !== 'object') return false
  if (Array.isArray(a) !== Array.isArray(b)) return false

  if (Array.isArray(a)) {
    return a.length === b.length && a.every((value, index) => jsonValuesEqual(value, b[index]))
  }

  const keys = new Set([...Object.keys(a), ...Object.keys(b)])

  return [...keys].every(key => jsonValuesEqual(a[key] ?? null, b[key] ?? null))
}
